import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Check, X, Clock, Wallet } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  getDeposits,
  updateDepositStatus,
  DepositData,
  formatCurrency,
  formatDate,
} from '@/features/admin/services/adminService'

export default function AdminDeposits() {
  const { t } = useTranslation()
  const [deposits, setDeposits] = useState<DepositData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filter, setFilter] = useState<string>('pending')
  const [processingId, setProcessingId] = useState<string | null>(null)

  useEffect(() => {
    loadDeposits()
  }, [filter])

  const loadDeposits = async () => {
    setIsLoading(true)
    const data = await getDeposits(filter === 'all' ? undefined : filter)
    setDeposits(data)
    setIsLoading(false)
  }

  const handleApprove = async (deposit: DepositData) => {
    setProcessingId(deposit.id)
    await updateDepositStatus(deposit.id, 'approved', deposit.userId, deposit.amount)
    loadDeposits()
    setProcessingId(null)
  }

  const handleReject = async (deposit: DepositData) => {
    if (!confirm('¿Rechazar este depósito?')) return
    setProcessingId(deposit.id)
    await updateDepositStatus(deposit.id, 'rejected', deposit.userId, deposit.amount)
    loadDeposits()
    setProcessingId(null)
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="text-xs bg-yellow-900/50 text-yellow-400 px-2 py-1 rounded flex items-center gap-1"><Clock className="w-3 h-3" /> Pendiente</span>
      case 'approved':
        return <span className="text-xs bg-green-900/50 text-green-400 px-2 py-1 rounded flex items-center gap-1"><Check className="w-3 h-3" /> Aprobado</span>
      case 'rejected':
        return <span className="text-xs bg-red-900/50 text-red-400 px-2 py-1 rounded flex items-center gap-1"><X className="w-3 h-3" /> Rechazado</span>
      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-cinzel text-2xl text-casino-gold">{t('admin.deposits')}</h1>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger value="pending">Pendientes</TabsTrigger>
          <TabsTrigger value="approved">Aprobados</TabsTrigger>
          <TabsTrigger value="rejected">Rechazados</TabsTrigger>
          <TabsTrigger value="all">Todos</TabsTrigger>
        </TabsList>

        <TabsContent value={filter} className="mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-casino-gold" />
            </div>
          ) : deposits.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Wallet className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                <p className="text-gray-400">No hay depósitos {filter === 'pending' ? 'pendientes' : ''}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {deposits.map((deposit) => (
                <Card key={deposit.id} className={deposit.status === 'pending' ? 'border-yellow-500/30' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <p className="font-medium">{deposit.userName || 'Usuario'}</p>
                          {getStatusBadge(deposit.status)}
                        </div>
                        <p className="text-sm text-gray-400">{deposit.userEmail}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {deposit.method} {deposit.reference && `- Ref: ${deposit.reference}`}
                        </p>
                      </div>

                      <div className="text-right mr-4">
                        <p className="text-xl font-bold text-green-400">
                          {formatCurrency(deposit.amount)}
                        </p>
                        <p className="text-xs text-gray-500">{formatDate(deposit.createdAt)}</p>
                      </div>

                      {deposit.status === 'pending' && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleApprove(deposit)}
                            disabled={processingId === deposit.id}
                            className="border-green-600 text-green-500 hover:bg-green-900/20"
                          >
                            {processingId === deposit.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReject(deposit)}
                            disabled={processingId === deposit.id}
                            className="border-red-600 text-red-500 hover:bg-red-900/20"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
